import React from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { create } from 'zustand';
import { useTranslation } from 'react-i18next';

import { Encounter } from 'api/encounters/types';
import { Vulnerability } from 'api/vulnerabilities/type';

type ViewModalSelection = Vulnerability | Encounter;

interface ViewModalState {
  isOpen: boolean;
  selectedItem: ViewModalSelection | null;
  onViewCodeModalClose: () => void;
  onViewCodeModalOpen: (item: ViewModalSelection) => void;
}

const isVulnerability = (item: ViewModalSelection): item is Vulnerability => item && typeof item.status === 'object';

export const useViewModalStore = create<ViewModalState>(set => ({
  isOpen: false,
  selectedItem: null,

  onViewCodeModalClose: () => {
    set({ isOpen: false, selectedItem: null });
  },
  onViewCodeModalOpen: item => {
    set({ isOpen: true, selectedItem: item });
  },
}));

const ViewModal: React.FC = () => {
  const { isOpen, onViewCodeModalClose, selectedItem } = useViewModalStore();
  const { t } = useTranslation();
  if (!selectedItem) return null;

  const title = isVulnerability(selectedItem)
    ? t('ViewModal.titleVulnerability', { vulnerabilityId: selectedItem.id })
    : t('ViewModal.titleSighting', { sightingId: selectedItem.id });

  const beforePhotos = selectedItem.before_photos || [];
  const afterPhotos = selectedItem.after_photos || [];

  return (
    <Modal isOpen={isOpen} toggle={onViewCodeModalClose} size="lg">
      <ModalHeader toggle={onViewCodeModalClose} className="border-bottom py-3">
        <div className="text-purple">{title}</div>
      </ModalHeader>
      <ModalBody className="text-center p-3">
        {beforePhotos.length > 0 && (
          <div className="mb-4">
            <h6 className="text-start font-weight-bold">{t('ViewModal.beforePhotos')}</h6>
            {beforePhotos.map((photoUrl, index) => (
              <img
                key={`before-${index}`}
                src={photoUrl}
                alt={`Before ${index + 1}`}
                className="mb-2 rounded img-thumbnail"
                style={{ maxHeight: '500px', objectFit: 'cover' }}
              />
            ))}
          </div>
        )}

        {afterPhotos.length > 0 && (
          <div className="mb-4">
            <h6 className="text-start font-weight-bold">{t('ViewModal.afterPhotos')}</h6>
            {afterPhotos.map((photoUrl, index) => (
              <img
                key={`after-${index}`}
                src={photoUrl}
                alt={`After ${index + 1}`}
                className=" mb-2 rounded img-thumbnail"
                style={{ maxHeight: '500px', objectFit: 'cover' }}
              />
            ))}
          </div>
        )}

        {!beforePhotos.length && !afterPhotos.length && <p className="text-muted my-4">{t('ViewModal.noPhotos')}</p>}
      </ModalBody>
    </Modal>
  );
};

export default ViewModal;
